// LiveLearnTicker — Home R₿L section · real-time burn aggregator.
// Sources /api/burns/ticker (rbl_burns + voluntary_burns). Rotates between
// an aggregate message and individual burns. Refreshes every 60s, rotates
// every 4s. Never shows a static placeholder.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Flame, Radio } from "lucide-react";
import { useLanguage } from "../../i18n/LanguageContext";
import { API } from "../../lib/api";

const T = {
  es: {
    live: "EN VIVO",
    total: (n) => `Total quemado: ${n} R₿L — Learn-to-Earn activo`,
    burned: "quemó",
    loading: "Cargando quemas en vivo…",
  },
  en: {
    live: "LIVE",
    total: (n) => `Total burned: ${n} R₿L — Learn-to-Earn active`,
    burned: "burned",
    loading: "Loading live burns…",
  },
  sk: {
    live: "NAŽIVO",
    total: (n) => `Spálené spolu: ${n} R₿L — Learn-to-Earn aktívne`,
    burned: "spálil",
    loading: "Načítavanie spaľovania…",
  }, de: { live: "LIVE", total: (n) => `Insgesamt verbrannt: ${n} R₿L — Learn-to-Earn aktiv`, burned: "verbrannt", loading: "Lade Live-Verbrennungen…" }, "zh-CN": { live: "直播", total: (n) => `已销毁总量：${n} R₿L — Learn-to-Earn 已激活`, burned: "已销毁", loading: "加载实时销毁中…" },
};

const fmt = (n) => Number(n || 0).toLocaleString("es-ES");

export const LiveLearnTicker = () => {
  const { lang } = useLanguage();
  const t = T[lang] || T.en || T.es;
  const [data, setData] = useState({ total_burned_rbl: 0, items: [] });
  const [idx, setIdx] = useState(0);
  const fetchRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API}/burns/ticker?limit=5`);
      if (!r.ok) return;
      const d = await r.json();
      setData({
        total_burned_rbl: Number(d.total_burned_rbl || 0),
        items: Array.isArray(d.items) ? d.items : [],
      });
    } catch { /* silent — marketing widget, never block */ }
  }, []);

  // initial fetch + refresh every 60s
  useEffect(() => {
    load();
    fetchRef.current = setInterval(load, 60_000);
    return () => clearInterval(fetchRef.current);
  }, [load]);

  // Build rotating message list: [aggregate, ...individual burns]
  const messages = [];
  messages.push({ kind: "total", amount: data.total_burned_rbl });
  for (const it of data.items) {
    if (!it || !it.amount_rbl) continue;
    messages.push({
      kind: "item",
      wallet: it.wallet,
      amount: it.amount_rbl,
    });
  }

  // Rotate every 4s
  useEffect(() => {
    if (messages.length < 2) return undefined;
    const id = setInterval(() => setIdx((i) => (i + 1) % messages.length), 4000);
    return () => clearInterval(id);
  }, [messages.length]);

  if (messages.length === 0) {
    return (
      <div
        className="mx-auto mt-2 flex max-w-2xl items-center gap-2 rounded-full border border-orange-400/20 bg-black/40 px-4 py-1.5 text-[11px] text-white/40 backdrop-blur"
        data-testid="live-learn-ticker-loading"
      >
        <Radio className="h-3 w-3 animate-pulse text-orange-400" />
        {t.loading}
      </div>
    );
  }

  const cur = messages[idx % messages.length];

  return (
    <div
      className="relative mx-auto flex max-w-2xl items-center gap-3 rounded-full border border-orange-400/30 bg-gradient-to-r from-orange-500/10 via-black/60 to-violet-500/10 px-4 py-1.5 backdrop-blur"
      data-testid="live-learn-ticker"
    >
      <span className="relative flex h-2 w-2 shrink-0">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-orange-400 opacity-75" />
        <span className="relative inline-flex h-2 w-2 rounded-full bg-orange-500" />
      </span>
      <span className="hidden font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-orange-300 sm:inline">
        {t.live}
      </span>
      <div className="min-w-0 flex-1 overflow-hidden">
        <AnimatePresence mode="wait">
          <motion.div
            key={`${cur.kind}-${cur.wallet || "total"}-${idx}`}
            initial={{ y: 10, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -10, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="flex items-center gap-2 truncate text-xs text-white/90"
          >
            <Flame className="h-3.5 w-3.5 shrink-0 text-orange-300" />
            {cur.kind === "total" ? (
              <span className="truncate" data-testid="ticker-burn-total">
                🔥 {t.total(fmt(cur.amount))}
              </span>
            ) : (
              <span className="truncate" data-testid="ticker-burn-item">
                🔥 <span className="font-mono text-white">{cur.wallet}</span>{" "}
                <span className="text-orange-200">{t.burned}</span>{" "}
                <span className="font-bold text-orange-300">{fmt(cur.amount)} R₿L</span>
              </span>
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};

export default LiveLearnTicker;
